import React, { useRef, useEffect, useCallback } from 'react';
import './audio-ripple.scss';

interface AudioRippleProps {
  /** 0–1.5 range, drives wave amplitude */
  outputLevel: number;
  /** Whether the agent is currently speaking */
  isSpeaking: boolean;
  /** Diameter of the circular overlay in px */
  size?: number;
  /** Optional real-time ref that updates every frame — bypasses throttled prop for smooth animation */
  outputLevelRef?: React.MutableRefObject<number>;
}

// Each wave line has its own frequency, speed, and base amplitude
const WAVE_LINES = [
  { freq: 1.2, speed: 0.8, amp: 0.28, opacity: 0.9 },
  { freq: 1.8, speed: -1.1, amp: 0.22, opacity: 0.7 },
  { freq: 2.5, speed: 1.4, amp: 0.18, opacity: 0.5 },
  { freq: 0.9, speed: -0.6, amp: 0.15, opacity: 0.35 },
  { freq: 3.2, speed: 1.9, amp: 0.12, opacity: 0.25 },
];

const AudioRipple: React.FC<AudioRippleProps> = ({
  outputLevel,
  isSpeaking,
  size = 152,
  outputLevelRef: externalLevelRef,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const timeRef = useRef(0);
  const smoothLevelRef = useRef(0);

  // Store fast-changing props in refs so the draw loop never needs recreating
  const internalLevelRef = useRef(outputLevel);
  const isSpeakingRef = useRef(isSpeaking);
  useEffect(() => {
    internalLevelRef.current = outputLevel;
  }, [outputLevel]);
  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);

  // Use external ref (full frame-rate) if provided, otherwise fall back to internal (throttled)
  const levelRef = externalLevelRef || internalLevelRef;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = size;
    const h = size;

    // Drive directly from output level — naturally ~0 when silent
    const targetLevel = Math.min(levelRef.current, 1.5);
    const delta = targetLevel - smoothLevelRef.current;
    // Gentle smoothing so natural audio variation shows through
    smoothLevelRef.current += delta * (delta > 0 ? 0.12 : 0.18);
    const baseLevel = smoothLevelRef.current;

    timeRef.current += 0.016; // ~60fps step
    const t = timeRef.current;

    // Breathing modulation — overlapping sine waves create organic pulsing
    // Only active when there's audio (baseLevel > 0), scales with level
    const breathe =
      baseLevel > 0.02
        ? (Math.sin(t * 3.5) * 0.25 + Math.sin(t * 5.8) * 0.15) * baseLevel
        : 0;
    const level = Math.max(0, baseLevel + breathe);

    ctx.clearRect(0, 0, w * dpr, h * dpr);
    ctx.save();
    ctx.scale(dpr, dpr);

    const centreY = h / 2;

    for (const wave of WAVE_LINES) {
      const amplitude = wave.amp * h * 0.4 * (0.05 + level * 0.95);
      const phase = t * wave.speed * 3;

      // Interpolate colour from resting blue toward bright white-blue when active
      const r = Math.round(151 + (255 - 151) * level);
      const g = Math.round(213 + (255 - 213) * level);
      const b = 252;

      ctx.beginPath();
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${wave.opacity})`;
      ctx.lineWidth = 1.5 + level * 0.8;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';

      for (let x = 0; x <= w; x += 1) {
        const normalX = (x / w) * Math.PI * 2 * wave.freq;
        const y =
          centreY +
          Math.sin(normalX + phase) * amplitude +
          Math.sin(normalX * 0.5 + phase * 0.7) * amplitude * 0.3;

        if (x === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.stroke();
    }

    ctx.restore();
    animRef.current = requestAnimationFrame(draw);
  }, [size]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [size, draw]);

  return (
    <canvas
      ref={canvasRef}
      className='audio-wave-canvas'
      style={{ width: size, height: size }}
    />
  );
};

export default React.memo(AudioRipple);
